import { Emitter } from "@core/events/emitter";
import type { IUndoRedo, UndoRedoCommand } from "./IUndoRedo";

/**
 * `UndoRedo` — in-memory command stack implementation of {@link IUndoRedo}.
 *
 * Unity reference: `Models/UndoRedo.cs` (Battlehub `IRTE` wrapper). We drop
 * the Battlehub dependency and the Unity-object-aware overloads
 * (`Record(GameObject)`, expression-based member records); callers compose
 * their own redo/undo lambdas, which is more honest about what is
 * persisted vs visual-only.
 *
 * Group semantics match Unity: nested `beginRecordGroup` / `endRecordGroup`
 * collapse a burst of `record` calls into one stack entry. While an
 * undo/redo is replaying, `isRecording` is `false` so re-entrant `record`
 * calls are silently dropped (matches Unity's `m_isRecording` flag).
 */
export class UndoRedo implements IUndoRedo {
  readonly onBeforeUndo = new Emitter<void>();
  readonly onAfterUndo = new Emitter<void>();
  readonly onBeforeRedo = new Emitter<void>();
  readonly onAfterRedo = new Emitter<void>();
  readonly onStateChanged = new Emitter<void>();

  private readonly m_undoStack: UndoRedoCommand[] = [];
  private readonly m_redoStack: UndoRedoCommand[] = [];

  private m_isRecording = true;
  private m_groupDepth = 0;
  private m_groupLabel: string | undefined;
  private m_groupBuffer: UndoRedoCommand[] | null = null;

  get isRecording(): boolean {
    return this.m_isRecording;
  }

  get canUndo(): boolean {
    return this.m_undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.m_redoStack.length > 0;
  }

  record(command: UndoRedoCommand): void {
    if (!this.m_isRecording) return;

    try {
      command.redo();
    } catch (err) {
      console.error("[UndoRedo] command.redo() threw; not recorded", err);
      return;
    }

    if (this.m_groupBuffer) {
      this.m_groupBuffer.push(command);
      return;
    }

    this.m_undoStack.push(command);
    this.dropRedoStack();
    this.onStateChanged.emit();
  }

  beginRecordGroup(label?: string): void {
    if (this.m_groupDepth === 0) {
      this.m_groupBuffer = [];
      this.m_groupLabel = label;
    }
    this.m_groupDepth += 1;
  }

  endRecordGroup(): void {
    if (this.m_groupDepth === 0) {
      console.warn("[UndoRedo] endRecordGroup() with no matching begin");
      return;
    }
    this.m_groupDepth -= 1;
    if (this.m_groupDepth > 0) return;

    const buffer = this.m_groupBuffer ?? [];
    const label = this.m_groupLabel;
    this.m_groupBuffer = null;
    this.m_groupLabel = undefined;

    if (buffer.length === 0) return;

    if (buffer.length === 1) {
      this.m_undoStack.push(buffer[0]!);
    } else {
      const grouped: UndoRedoCommand = {
        label,
        redo: () => {
          for (const c of buffer) c.redo();
        },
        undo: () => {
          for (let i = buffer.length - 1; i >= 0; i -= 1) buffer[i]!.undo();
        },
      };
      this.m_undoStack.push(grouped);
    }
    this.dropRedoStack();
    this.onStateChanged.emit();
  }

  undo(): void {
    if (!this.canUndo) return;
    const command = this.m_undoStack.pop()!;
    this.onBeforeUndo.emit();
    const wasRecording = this.m_isRecording;
    this.m_isRecording = false;
    try {
      command.undo();
      this.m_redoStack.push(command);
    } catch (err) {
      console.error("[UndoRedo] undo() threw; stack may be inconsistent", err);
    } finally {
      this.m_isRecording = wasRecording;
    }
    this.onAfterUndo.emit();
    this.onStateChanged.emit();
  }

  redo(): void {
    if (!this.canRedo) return;
    const command = this.m_redoStack.pop()!;
    this.onBeforeRedo.emit();
    const wasRecording = this.m_isRecording;
    this.m_isRecording = false;
    try {
      command.redo();
      this.m_undoStack.push(command);
    } catch (err) {
      console.error("[UndoRedo] redo() threw; stack may be inconsistent", err);
    } finally {
      this.m_isRecording = wasRecording;
    }
    this.onAfterRedo.emit();
    this.onStateChanged.emit();
  }

  clear(): void {
    if (this.m_undoStack.length === 0 && this.m_redoStack.length === 0) return;
    this.m_undoStack.length = 0;
    this.m_redoStack.length = 0;
    this.m_groupDepth = 0;
    this.m_groupBuffer = null;
    this.m_groupLabel = undefined;
    this.onStateChanged.emit();
  }

  private dropRedoStack(): void {
    if (this.m_redoStack.length > 0) this.m_redoStack.length = 0;
  }
}
